import { useState } from 'react';
import { CaseUpper, CornerDownLeft } from 'lucide-react';
import { useEditor } from '../../state/store';
import { BLOCK_FONT_STYLES, BlockFontStyle, renderBlockText } from '../../tools/text';
import { CellWrite } from '../../tools/types';
import { Field, Hint, Panel } from '../widgets/common';
import { Select } from '../widgets/primitives';

/**
 * Text tools.
 *
 * Plain typing happens on the canvas with the Text tool; this panel covers the
 * thing you cannot do by typing — stamping oversized letterforms built out of
 * block characters.
 */
export function TextPanel(): JSX.Element {
  const caret = useEditor((s) => s.caret);
  const cursor = useEditor((s) => s.cursor);
  const [text, setText] = useState('HELLO');
  const [style, setStyle] = useState<BlockFontStyle>('half');
  const [spacing, setSpacing] = useState(1);

  const lines = renderBlockText(text || ' ', style, spacing);

  const stamp = () => {
    const store = useEditor.getState();
    const origin = caret ?? cursor ?? { x: 0, y: 0 };
    const brush = store.brush;
    const writes: CellWrite[] = [];
    lines.forEach((line, y) => {
      [...line].forEach((ch, x) => {
        if (ch === ' ') return;
        writes.push({
          x: origin.x + x,
          y: origin.y + y,
          cell: { char: ch, fg: brush.fg, bg: brush.bg, attrs: brush.attrs },
        });
      });
    });
    if (writes.length === 0) {
      store.notify('Nothing to stamp.', 'warn');
      return;
    }
    store.writeCells(writes, 'Block text');
    store.notify(`Stamped "${text}" at ${origin.x}, ${origin.y}.`, 'success');
  };

  return (
    <>
      <Panel title="Typing" collapsible={false}>
        <Hint>
          Pick the Text tool, click the canvas to place a caret, then type. Enter returns to the
          column you started in; Esc drops the caret.
        </Hint>
        <div className="row" style={{ marginTop: 8 }}>
          <CornerDownLeft size={14} className="faint" />
          <span className="faint">
            {caret ? `Caret at ${caret.x}, ${caret.y}` : 'No caret placed'}
          </span>
        </div>
      </Panel>

      <Panel title="Block text">
        <input
          className="input"
          value={text}
          maxLength={40}
          placeholder="Text to stamp"
          onChange={(e) => setText(e.target.value)}
        />
        <Field label="Style">
          <Select
            ariaLabel="Style"
            value={style}
            options={BLOCK_FONT_STYLES.map((s) => ({ value: s, label: s }))}
            onChange={setStyle}
          />
        </Field>
        <Field label="Spacing">
          <input
            type="range"
            min={0}
            max={4}
            value={spacing}
            style={{ width: '100%' }}
            onChange={(e) => setSpacing(Number(e.target.value))}
          />
        </Field>

        <pre className="terminal-preview" style={{ fontSize: 9, lineHeight: 1.1, maxHeight: 110 }}>
          {lines.join('\n')}
        </pre>

        <button className="btn primary tall" style={{ width: '100%', marginTop: 8 }} onClick={stamp}>
          <CaseUpper size={15} />
          Stamp at {caret ? `${caret.x}, ${caret.y}` : cursor ? `${cursor.x}, ${cursor.y}` : '0, 0'}
        </button>
        <Hint>
          {lines.length} rows × {lines[0]?.length ?? 0} columns. "half" packs two bitmap rows per
          terminal row, which usually reads best in a 24-row terminal.
        </Hint>
      </Panel>
    </>
  );
}
